/**
 * <versel-size-tools>: the "Find my size" quiz and the size chart dialog.
 *
 * Size lists and measurements come straight from the chart tables rendered by
 * blocks/versel-size-tools.liquid, so editing a chart in the theme editor updates the quiz too.
 */

const CHECK_ICON =
  '<svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14"><path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * Quiz questions per audience and category. `size` is the "what do you normally wear" question;
 * every other answer carries points, and the total moves the recommendation along the chart's size list.
 */
const MENS_QUIZ = {
  tops: {
    label: 'Tops',
    hint: 'T-shirts, shirts, polos, sweaters, jackets',
    questions: [
      { id: 'size', title: 'What size do you usually wear in tops?', text: 'Pick the size you buy most often.' },
      {
        id: 'fit',
        title: 'How do you like your tops to fit?',
        text: 'This tells us how much room you want.',
        options: [
          { label: 'Snug', hint: 'Close to the body', points: -1 },
          { label: 'True to size', hint: 'Classic, not tight or loose', points: 0 },
          { label: 'Relaxed', hint: 'Room to move', points: 1 },
        ],
      },
      {
        id: 'build',
        title: 'How would you describe your build?',
        text: 'Last one. It helps us get the chest and shoulders right.',
        options: [
          { label: 'Slim', hint: 'Narrow chest and shoulders', points: -1 },
          { label: 'Average', hint: 'In proportion', points: 0 },
          { label: 'Broad', hint: 'Wider chest, shoulders or belly', points: 1 },
        ],
      },
    ],
    shift: (points) => (points >= 2 ? 1 : points <= -2 ? -1 : 0),
  },
  bottoms: {
    label: 'Bottoms',
    hint: 'Pants, trousers, jeans, shorts',
    questions: [
      { id: 'size', title: 'What size do you usually wear in pants?', text: 'Pick the size you buy most often.' },
      {
        id: 'fit',
        title: 'How do you like your pants to fit?',
        text: 'This tells us how much room you want.',
        options: [
          { label: 'Slim', hint: 'Close through the leg', points: -1 },
          { label: 'Regular', hint: 'Straight and easy', points: 0 },
          { label: 'Relaxed', hint: 'Roomy through seat and thigh', points: 1 },
        ],
      },
      {
        id: 'build',
        title: 'How would you describe your thighs and seat?',
        text: 'It helps us balance the waist and leg fit.',
        options: [
          { label: 'Slim', points: -1 },
          { label: 'Average', points: 0 },
          { label: 'Athletic or fuller', points: 1 },
        ],
      },
      {
        id: 'height',
        title: 'How tall are you?',
        text: 'Last one. We use it to suggest your inseam.',
        options: [
          { label: 'Under 5\'9"', hint: 'Under 175 cm', points: 0, length: 'regular' },
          { label: '5\'9" – 6\'0"', hint: '175 – 183 cm', points: 0, length: 'regular' },
          { label: 'Over 6\'0"', hint: 'Over 183 cm', points: 0, length: 'long' },
        ],
      },
    ],
    shift: (points) => (points >= 2 ? 1 : points <= -2 ? -1 : 0),
  },
  shoes: {
    label: 'Shoes',
    hint: 'Sneakers, loafers, boots',
    questions: [
      { id: 'size', title: "What's your usual shoe size?", text: 'Pick the US size you normally wear.' },
      {
        id: 'width',
        title: 'How would you describe your feet?',
        text: 'Tap the shape closest to yours. This is how we get the width right.',
        options: [
          { label: 'Narrow', points: 0 },
          { label: 'Medium', points: 0 },
          { label: 'Wide', points: 1 },
          { label: 'Extra wide', points: 2 },
        ],
      },
      {
        id: 'socks',
        title: 'What will you wear them with?',
        text: 'Last one. Sock thickness changes the fit.',
        options: [
          { label: 'Bare feet or thin socks', points: 0 },
          { label: 'Everyday socks', points: 0 },
          { label: 'Thick or cushioned socks', points: 1 },
        ],
      },
    ],
    // Each step is one row in the chart, i.e. half a size.
    shift: (points) => (points >= 3 ? 2 : points >= 1 ? 1 : 0),
  },
};

const WOMENS_QUIZ = {
  tops: {
    label: 'Tops',
    hint: 'Blouses, tees, knits, jackets',
    questions: [
      { id: 'size', title: 'What size do you usually wear in tops?', text: 'Pick the size you buy most often.' },
      {
        id: 'fit',
        title: 'How do you like your tops to fit?',
        text: 'This tells us how much room you want.',
        options: [
          { label: 'Fitted', hint: 'Close to the body', points: -1 },
          { label: 'True to size', hint: 'Not tight, not loose', points: 0 },
          { label: 'Relaxed', hint: 'Easy or slightly oversized', points: 1 },
        ],
      },
      {
        id: 'bust',
        title: 'How would you describe your bust?',
        text: 'Last one. It helps us get the chest and shoulders right.',
        options: [
          { label: 'Smaller', points: -1 },
          { label: 'Average', points: 0 },
          { label: 'Fuller', hint: 'Tops often pull across the chest', points: 1 },
        ],
      },
    ],
    shift: (points) => (points >= 2 ? 1 : points <= -2 ? -1 : 0),
  },
  dresses: {
    label: 'Dresses',
    hint: 'Maxi, midi, mini, jumpsuits',
    questions: [
      { id: 'size', title: 'What size do you usually wear in dresses?', text: 'Pick the size you buy most often.' },
      {
        id: 'fit',
        title: 'How do you like your dresses to fit?',
        text: 'This tells us how much room you want.',
        options: [
          { label: 'Body-hugging', hint: 'Close through bust, waist and hips', points: -1 },
          { label: 'Fitted but comfortable', hint: 'Shaped, with a little ease', points: 0 },
          { label: 'Relaxed', hint: 'Flowy and easy', points: 1 },
        ],
      },
      {
        id: 'shape',
        title: 'Where do dresses usually feel tight on you?',
        text: 'Dresses fit to your fullest measurement, so this matters most.',
        options: [
          { label: 'Nowhere, they usually fit well', points: 0 },
          { label: 'Bust', points: 1 },
          { label: 'Waist', points: 1 },
          { label: 'Hips', points: 1 },
          { label: 'They usually feel loose', points: -1 },
        ],
      },
      {
        id: 'height',
        title: 'How tall are you?',
        text: "Last one. It tells us how longer styles will fall on you.",
        options: [
          { label: 'Under 5\'3"', hint: 'Under 160 cm', points: 0, length: 'petite' },
          { label: '5\'3" – 5\'7"', hint: '160 – 170 cm', points: 0, length: 'regular' },
          { label: 'Over 5\'7"', hint: 'Over 170 cm', points: 0, length: 'tall' },
        ],
      },
    ],
    shift: (points) => (points >= 2 ? 1 : points <= -2 ? -1 : 0),
  },
  bottoms: {
    label: 'Bottoms',
    hint: 'Pants, jeans, skirts, shorts',
    questions: [
      { id: 'size', title: 'What size do you usually wear in bottoms?', text: 'Pick the size you buy most often.' },
      {
        id: 'fit',
        title: 'How do you like your bottoms to fit?',
        text: 'This tells us how much room you want.',
        options: [
          { label: 'Close-fitting', hint: 'Skinny or body-skimming', points: -1 },
          { label: 'Regular', hint: 'Straight and easy', points: 0 },
          { label: 'Relaxed', hint: 'Wide-leg or loose', points: 1 },
        ],
      },
      {
        id: 'hips',
        title: 'How would you describe your hips and thighs?',
        text: 'Last one. It helps us balance the waist and hip fit.',
        options: [
          { label: 'Slim', points: -1 },
          { label: 'Average', points: 0 },
          { label: 'Curvy', hint: 'Bottoms often fit the waist but not the hips', points: 1 },
        ],
      },
    ],
    shift: (points) => (points >= 2 ? 1 : points <= -2 ? -1 : 0),
  },
  shoes: {
    label: 'Shoes',
    hint: 'Heels, flats, sneakers, boots',
    questions: [
      { id: 'size', title: "What's your usual shoe size?", text: "Pick the US women's size you normally wear." },
      {
        id: 'width',
        title: 'How would you describe your feet?',
        text: 'This is how we get the width right.',
        options: [
          { label: 'Narrow', points: 0 },
          { label: 'Medium', points: 0 },
          { label: 'Wide', points: 1 },
          { label: 'Extra wide', points: 2 },
        ],
      },
      {
        id: 'socks',
        title: 'What will you wear them with?',
        text: 'Last one. What you wear underneath changes the fit.',
        options: [
          { label: 'Bare feet', points: 0 },
          { label: 'Thin socks or tights', points: 0 },
          { label: 'Thick or cushioned socks', points: 1 },
        ],
      },
    ],
    // Each step is one row in the chart, i.e. one size.
    shift: (points) => (points >= 2 ? 1 : 0),
  },
};

/** Extra line on the dress result, by the height answer. */
const LENGTH_NOTES = {
  petite: 'At under 5\'3", maxi and midi styles may fall a little longer on you.',
  tall: 'At over 5\'7", maxi styles may sit just above the ankle.',
};

/** Chart columns that hold size labels, not measurements (matches blocks/versel-size-tools.liquid). */
const LABEL_HEADERS = ['size', 'us', 'uk', 'eu', 'us size', 'uk size', 'eu size'];

const escapeHTML = (value) =>
  String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

class VerselSizeTools extends HTMLElement {
  /** @type {string} */
  #category = '';
  /** @type {number} */
  #step = 0;
  /** @type {Record<string, any>} */
  #answers = {};

  connectedCallback() {
    this.settings = JSON.parse(this.querySelector('[data-vc-settings]')?.textContent || '{}');
    this.addEventListener('click', this.#onClick);
    this.#syncUnits();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get productCategory() {
    return this.dataset.category || 'tops';
  }

  get quiz() {
    return this.settings?.audience === 'women' ? WOMENS_QUIZ : MENS_QUIZ;
  }

  /** @param {'finder' | 'chart'} name */
  dialog(name) {
    return /** @type {any} */ (this.querySelector(`[data-vc-dialog="${name}"]`));
  }

  /** @param {'finder' | 'chart'} name */
  async open(name) {
    const other = name === 'finder' ? 'chart' : 'finder';
    const otherDialog = this.dialog(other);
    if (otherDialog?.refs?.dialog?.open) await otherDialog.closeDialog();
    if (name === 'finder' && this.#step === 0) this.#renderIntroProgress();
    this.dialog(name)?.showDialog();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const control = target.closest('button');
    if (!control) return;

    if (control.dataset.vcOpen) return void this.open(/** @type {any} */ (control.dataset.vcOpen));
    if (control.hasAttribute('data-vc-start')) return this.#renderCategoryStep();
    if (control.dataset.vcCategory) {
      this.#category = control.dataset.vcCategory;
      this.#answers = {};
      this.#step = 1;
      return this.#renderQuestion();
    }
    if (control.dataset.vcAnswer !== undefined) {
      const question = this.quiz[this.#category].questions[this.#step - 1];
      this.#answers[question.id] = control.dataset.vcAnswer;
      this.#step += 1;
      return this.#step > this.quiz[this.#category].questions.length ? this.#renderResult() : this.#renderQuestion();
    }
    if (control.hasAttribute('data-vc-back')) {
      this.#step -= 1;
      return this.#step <= 0 ? this.#renderCategoryStep() : this.#renderQuestion();
    }
    if (control.hasAttribute('data-vc-restart')) return this.#renderCategoryStep();
    if (control.dataset.vcSelectSize) return this.#selectSize(control.dataset.vcSelectSize);
    if (control.dataset.vcTab) return this.showTab(control.dataset.vcTab);
    if (control.dataset.vcUnit) return this.#setUnit(control.dataset.vcUnit);
  };

  /* ---------- Chart data ---------- */

  /** @param {string} category */
  chart(category) {
    const panel = this.querySelector(`[data-vc-panel="${category}"]`);
    const headers = [...(panel?.querySelectorAll('thead th') ?? [])].map((cell) => cell.textContent.trim());
    const rows = [...(panel?.querySelectorAll('tbody tr') ?? [])].map((row) =>
      [...row.children].map((cell) => /** @type {HTMLElement} */ (cell).dataset.original ?? cell.textContent.trim())
    );
    return { headers, rows, unit: panel?.getAttribute('data-unit') || 'none' };
  }

  /* ---------- Quiz ---------- */

  #setProgress(fraction) {
    const bar = this.querySelector('[data-vc-progress]');
    if (bar instanceof HTMLElement) bar.style.width = `${Math.round(fraction * 100)}%`;
  }

  #renderIntroProgress() {
    this.#setProgress(0.06);
  }

  /** @param {string} html */
  #render(html) {
    const root = this.querySelector('[data-vc-finder]');
    if (!root) return;
    root.innerHTML = `<div class="vc-finder__step">${html}</div>`;
    root.closest('dialog')?.scrollTo({ top: 0 });
    /** @type {HTMLElement | null} */ (root.querySelector('button'))?.focus({ preventScroll: true });
  }

  #renderCategoryStep() {
    this.#step = 0;
    this.#category = '';
    this.#setProgress(0.12);
    const options = Object.entries(this.quiz)
      .filter(([key]) => this.chart(key).rows.length > 0)
      .map(
        ([key, quiz]) => `
          <button type="button" class="vc-finder__option${key === this.productCategory ? ' is-suggested' : ''}" data-vc-category="${key}">
            <span><strong>${escapeHTML(this.settings.titles?.[key] || quiz.label)}</strong><br><small>${escapeHTML(quiz.hint)}</small></span>
            ${CHECK_ICON}
          </button>`
      )
      .join('');

    this.#render(`
      <h2 class="vc-finder__title">What are you shopping for?</h2>
      <p class="vc-finder__text">Choose what you'd like us to size.</p>
      <div class="vc-finder__options">${options}</div>
    `);
  }

  #renderQuestion() {
    const quiz = this.quiz[this.#category];
    const question = quiz.questions[this.#step - 1];
    const total = quiz.questions.length + 1;
    this.#setProgress(0.12 + (0.88 * this.#step) / total);

    let options = '';
    let grid = false;

    if (question.id === 'size') {
      const { headers, rows } = this.chart(this.#category);
      grid = true;
      options = rows
        .map((row) => {
          const hint = this.#category === 'shoes' || !row[1] ? '' : `<small>${escapeHTML(headers[1] || '')} ${escapeHTML(row[1])}</small>`;
          return `<button type="button" class="vc-finder__option" data-vc-answer="${escapeHTML(row[0])}">${escapeHTML(row[0])}${hint}</button>`;
        })
        .join('');
    } else {
      options = question.options
        .map(
          (option, index) => `
            <button type="button" class="vc-finder__option" data-vc-answer="${index}">
              <span>${escapeHTML(option.label)}${option.hint ? `<br><small>${escapeHTML(option.hint)}</small>` : ''}</span>
              ${CHECK_ICON}
            </button>`
        )
        .join('');
    }

    this.#render(`
      <h2 class="vc-finder__title">${escapeHTML(question.title)}</h2>
      <p class="vc-finder__text">${escapeHTML(question.text)}</p>
      <div class="vc-finder__options${grid ? ' vc-finder__options--grid' : ''}">${options}</div>
      <button type="button" class="vc-finder__back" data-vc-back>Back</button>
    `);
  }

  #recommendation() {
    const quiz = this.quiz[this.#category];
    const { headers, rows } = this.chart(this.#category);
    const sizes = rows.map((row) => row[0]);
    const usualIndex = Math.max(0, sizes.indexOf(this.#answers.size));

    let points = 0;
    let length = '';
    for (const question of quiz.questions) {
      if (!question.options) continue;
      const option = question.options[Number(this.#answers[question.id])];
      points += option?.points ?? 0;
      if (option?.length) length = option.length;
    }

    const index = Math.min(sizes.length - 1, Math.max(0, usualIndex + quiz.shift(points)));
    return { size: sizes[index], usual: sizes[usualIndex], step: index - usualIndex, headers, row: rows[index], length };
  }

  #renderResult() {
    this.#setProgress(1);
    const { size, usual, step, headers, row, length } = this.#recommendation();

    let specs = headers
      .slice(1)
      .map((header, index) => ({ header, value: row[index + 1] }))
      .filter(({ value }) => value);

    // Bottoms: show only the inseam that matches their height.
    if (this.#category === 'bottoms' && length) {
      specs = specs.filter(({ header }) => {
        const name = header.toLowerCase();
        if (!name.includes('inseam')) return true;
        return length === 'long' ? name.includes('long') : !name.includes('long');
      });
    }

    const unit = this.chart(this.#category).unit;
    const suffix = unit === 'in' ? '"' : unit === 'cm' ? ' cm' : '';
    const specsHTML = specs
      .map(({ header, value }) => `<div><dt>${escapeHTML(header)}</dt><dd>${escapeHTML(value)}${/\d$/.test(value) && !LABEL_HEADERS.includes(header.toLowerCase()) ? suffix : ''}</dd></div>`)
      .join('');

    const sizeInput = this.#category === this.productCategory ? this.#findSizeInput(size) : null;
    let note = step
      ? `We'd go ${step > 0 ? 'up' : 'down'} from your usual ${escapeHTML(usual)} for the fit you described.`
      : escapeHTML(this.settings.resultNote || '');
    if (this.#category === 'dresses' && LENGTH_NOTES[length]) note += ` ${escapeHTML(LENGTH_NOTES[length])}`;

    this.#render(`
      <span class="vc-finder__result-label">Your size</span>
      <p class="vc-finder__result-size">${escapeHTML(size)}</p>
      <p class="vc-finder__text">${note}</p>
      ${specsHTML ? `<dl class="vc-finder__specs">${specsHTML}</dl>` : ''}
      ${
        sizeInput
          ? `<button type="button" class="vc-finder__primary" data-vc-select-size="${escapeHTML(size)}">Select size ${escapeHTML(size)}</button>`
          : `<button type="button" class="vc-finder__primary" data-vc-open="chart">View size chart</button>`
      }
      <div class="vc-finder__links">
        <button type="button" class="vc-finder__back" data-vc-restart>Start over</button>
        ${sizeInput ? '<button type="button" class="vc-finder__back" data-vc-open="chart">View size chart</button>' : ''}
      </div>
    `);

    this.showTab(this.#category, size);
  }

  /** @param {string} size */
  #findSizeInput(size) {
    const scope = this.closest('product-information, .product-information, main') ?? document;
    const inputs = scope.querySelectorAll('variant-picker fieldset input[type="radio"]');
    const wanted = size.trim().toLowerCase();
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const option = (input.dataset.optionName || '').toLowerCase();
      if (!/size|taille|größe/.test(option)) continue;
      if (input.value.trim().toLowerCase() === wanted && input.dataset.optionAvailable !== 'false') return input;
    }
    return null;
  }

  /** @param {string} size */
  async #selectSize(size) {
    const input = this.#findSizeInput(size);
    await this.dialog('finder')?.closeDialog();
    if (input && !input.checked) input.click();
  }

  /* ---------- Size chart ---------- */

  /**
   * @param {string} category
   * @param {string} [highlightSize]
   */
  showTab(category, highlightSize) {
    for (const tab of this.querySelectorAll('[data-vc-tab]')) {
      tab.setAttribute('aria-selected', String(/** @type {HTMLElement} */ (tab).dataset.vcTab === category));
    }
    for (const panel of this.querySelectorAll('[data-vc-panel]')) {
      const element = /** @type {HTMLElement} */ (panel);
      element.hidden = element.dataset.vcPanel !== category;
      for (const row of element.querySelectorAll('tr[data-vc-size]')) {
        row.classList.toggle('is-highlighted', Boolean(highlightSize) && /** @type {HTMLElement} */ (row).dataset.vcSize === highlightSize);
      }
    }
    this.#syncUnits();
  }

  get #activePanel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-vc-panel]:not([hidden])'));
  }

  #syncUnits() {
    const units = /** @type {HTMLElement | null} */ (this.querySelector('[data-vc-units]'));
    const panel = this.#activePanel;
    if (!units || !panel) return;
    const base = panel.dataset.unit;
    units.hidden = base !== 'in' && base !== 'cm';
    const current = panel.dataset.current || base;
    for (const button of units.querySelectorAll('[data-vc-unit]')) {
      button.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (button).dataset.vcUnit === current));
    }
  }

  /** @param {string} unit */
  #setUnit(unit) {
    const panel = this.#activePanel;
    if (!panel) return;
    const base = panel.dataset.unit;
    if (base !== 'in' && base !== 'cm') return;
    panel.dataset.current = unit;

    const factor = base === 'in' ? 2.54 : 1 / 2.54;
    for (const cell of panel.querySelectorAll('[data-vc-measure]')) {
      const element = /** @type {HTMLElement} */ (cell);
      element.dataset.original ??= element.textContent.trim();
      element.textContent =
        unit === base
          ? element.dataset.original
          : element.dataset.original.replace(/\d+(?:\.\d+)?/g, (number) => String(Math.round(Number(number) * factor * 10) / 10));
    }
    this.#syncUnits();
  }
}

if (!customElements.get('versel-size-tools')) {
  customElements.define('versel-size-tools', VerselSizeTools);
}
